/**
 * Préchargement intelligent des données pour CareWashGo
 * Version 2.0 - Cache amélioré avec TTL et stratégies avancées
 */

import { getCachedData, cacheData } from './offlineStorage';
import { Capacitor } from '@capacitor/core';

const API = process.env.REACT_APP_BACKEND_URL || 'https://carewashgo.app';

// Détecte si on est sur une plateforme native (iOS/Android)
const IS_NATIVE = Capacitor.isNativePlatform();

/**
 * Cache en mémoire avec TTL
 */
class MemoryCache {
  constructor() {
    this.data = new Map();
    this.timestamps = new Map();
    this.TTL = 5 * 60 * 1000; // 5 minutes par défaut
  }

  set(key, value, ttl = this.TTL) {
    this.data.set(key, value);
    this.timestamps.set(key, { created: Date.now(), ttl });
  }

  get(key) {
    const meta = this.timestamps.get(key);
    if (!meta) return null;
    
    if (Date.now() - meta.created > meta.ttl) {
      this.data.delete(key);
      this.timestamps.delete(key);
      return null;
    }
    return this.data.get(key);
  }

  has(key) {
    return this.get(key) !== null;
  }

  invalidate(key) {
    this.data.delete(key);
    this.timestamps.delete(key);
  }

  clear() {
    this.data.clear();
    this.timestamps.clear();
  }
}

const cache = new MemoryCache();

// File de requêtes en cours pour éviter les doublons
const pendingRequests = new Map();

/**
 * Fetch avec déduplication et cache
 * Sur native, retourne null car fetch avec credentials ne fonctionne pas
 */
async function cachedFetch(url, cacheKey, ttl = 5 * 60 * 1000) {
  // Sur native, désactiver le prefetch (les données seront chargées normalement)
  if (IS_NATIVE) {
    return null;
  }

  // Vérifier le cache mémoire
  const cached = cache.get(cacheKey);
  if (cached) {
    return cached;
  }

  // Vérifier si une requête est déjà en cours
  if (pendingRequests.has(cacheKey)) {
    return pendingRequests.get(cacheKey);
  }

  // Vérifier IndexedDB (cache persistant)
  try {
    const indexedCached = await getCachedData(cacheKey);
    if (indexedCached) {
      cache.set(cacheKey, indexedCached, ttl);
      return indexedCached;
    }
  } catch (e) {
    // IndexedDB non disponible
  }

  // Faire la requête
  const requestPromise = fetch(url, { credentials: 'include' })
    .then(async (response) => {
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const data = await response.json();
      
      // Mettre en cache
      cache.set(cacheKey, data, ttl);
      
      // Persister dans IndexedDB
      try {
        await cacheData(cacheKey, data, ttl);
      } catch (e) {
        // IndexedDB non disponible
      }
      
      return data;
    })
    .finally(() => {
      pendingRequests.delete(cacheKey);
    });

  pendingRequests.set(cacheKey, requestPromise);
  return requestPromise;
}

/**
 * Précharge les données utilisateur
 */
export async function prefetchUserData() {
  try {
    return await cachedFetch(
      `${API}/api/auth/me`,
      'user_data',
      10 * 60 * 1000 // 10 minutes
    );
  } catch (error) {
    return null;
  }
}

/**
 * Précharge les badges
 */
export async function prefetchBadges() {
  try {
    return await cachedFetch(
      `${API}/api/badges/user`,
      'user_badges',
      15 * 60 * 1000 // 15 minutes
    );
  } catch (error) {
    return null;
  }
}

/**
 * Précharge le classement
 */
export async function prefetchLeaderboard() {
  try {
    return await cachedFetch(
      `${API}/api/leaderboard/global`,
      'leaderboard_global',
      5 * 60 * 1000 // 5 minutes
    );
  } catch (error) {
    return null;
  }
}

/**
 * Précharge les trophées
 */
export async function prefetchTrophies() {
  try {
    return await cachedFetch(
      `${API}/api/trophies`,
      'user_trophies',
      15 * 60 * 1000
    );
  } catch (error) {
    return null;
  }
}

/**
 * Précharge les objectifs
 */
export async function prefetchGoals() {
  try {
    return await cachedFetch(
      `${API}/api/goals`,
      'user_goals',
      5 * 60 * 1000
    );
  } catch (error) {
    return null;
  }
}

/**
 * Précharge les données essentielles au démarrage
 */
export async function prefetchEssentials() {
  
  const start = performance.now();
  
  const results = await Promise.allSettled([
    prefetchUserData(),
    prefetchBadges()
  ]);
  
  const duration = Math.round(performance.now() - start);
  
  return results;
}

/**
 * Précharge les données secondaires (après chargement initial)
 */
export async function prefetchSecondary() {
  // Attendre que la page soit idle
  if ('requestIdleCallback' in window) {
    requestIdleCallback(() => {
      Promise.allSettled([
        prefetchLeaderboard(),
        prefetchTrophies(),
        prefetchGoals()
      ]);
    }, { timeout: 3000 });
  } else {
    setTimeout(() => {
      Promise.allSettled([
        prefetchLeaderboard(),
        prefetchTrophies(),
        prefetchGoals()
      ]);
    }, 2000);
  }
}

/**
 * Précharge les routes pour navigation instantanée
 * DÉSACTIVÉ sur Android natif pour stabilité
 */
export function prefetchRoutes(routes) {
  // Ne pas exécuter sur Android natif
  const IS_ANDROID = Capacitor.getPlatform() === 'android';
  if (IS_ANDROID) return;
  
  // Vérifier que le DOM est disponible
  if (typeof document === 'undefined' || !document.head) return;
  if (typeof window === 'undefined') return;
  
  if ('requestIdleCallback' in window) {
    requestIdleCallback(() => {
      try {
        routes.forEach(route => {
          // Précharger le chunk JS
          const link = document.createElement('link');
          link.rel = 'prefetch';
          link.href = route;
          document.head.appendChild(link);
        });
      } catch (e) {
        console.warn('[Prefetch] Error prefetching routes:', e);
      }
    });
  }
}

/**
 * Hook pour utiliser le cache
 */
export function useCache(key) {
  return cache.get(key);
}

/**
 * Invalider une entrée du cache
 */
export function invalidateCache(key) {
  cache.invalidate(key);
}

/**
 * Clear tout le cache
 */
export function clearCache() {
  cache.clear();
}

/**
 * Précharger des images
 */
export function preloadImages(urls) {
  return Promise.all(
    urls.map(url => {
      return new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = resolve;
        img.onerror = reject;
        img.src = url;
      });
    })
  );
}

export default {
  prefetchUserData,
  prefetchBadges,
  prefetchLeaderboard,
  prefetchTrophies,
  prefetchGoals,
  prefetchEssentials,
  prefetchSecondary,
  prefetchRoutes,
  useCache,
  invalidateCache,
  clearCache,
  preloadImages
};
